import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as SwalHandled from '../../../../shared/utils/swalHandler';
import './wallet.css';
import {
  fetchUserCards,
  deleteUserCard,
  checkCardActiveSubscription,
} from '../../../../shared/actions/cardAction';
import AddCardModal from '../../CCbill/AddCardModal';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import useNavigation from '../../../../shared/utils/useNavigation';
import { isUserLoggedIn } from '../../../../shared/utils/function';
import { getCardIcon, formatCardNumber } from '../../../../shared/utils/paymentUtil';

const ModernWallet = () => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const hasLoadedCardsRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState('');
  const [isDeletingCard, setIsDeletingCard] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const checkActiveSubscription = useCallback(async (cardId) => {
    const response = await checkCardActiveSubscription(cardId);
    
    if (!response.success) {
      return false;
    }

    return Boolean(response.data);
  }, []);

  const loadCards = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) {
      setLoading(true);
    }
    setError('');

    try {
      const response = await fetchUserCards();

      if (response.success) {
        const fetchedCards = response.data || [];
        setCards(fetchedCards);
        
        // Check for active subscriptions across all cards
        if (fetchedCards.length > 0) {
          const checkAllSubscriptions = async () => {
            const subscriptionChecks = await Promise.all(
              fetchedCards.map((card) => checkActiveSubscription(card._id))
            );
            const hasAnyActive = subscriptionChecks.some((hasActive) => hasActive === true);
            setHasActiveSubscription(hasAnyActive);
          };
          checkAllSubscriptions();
        } else {
          setHasActiveSubscription(false);
        }
        return;
      }

      setError(response.error || 'Failed to fetch cards');
    } catch (err) {
      setError(err?.message || 'Failed to fetch cards');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [checkActiveSubscription]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate.toLogin();
      return;
    }

    if (hasLoadedCardsRef.current) {
      return;
    }

    hasLoadedCardsRef.current = true;
    loadCards();
  }, [isLoggedIn, loadCards, navigate]);

  const handleAddCard = () => {
    setShowAddModal(true);
  };

  const handleDelete = async (cardId) => {
    if (!cardId) {
      setError('Missing card identifier. Please refresh and try again.');
      return;
    }

    const hasActiveSubscription = await checkActiveSubscription(cardId);

    if (hasActiveSubscription) {
      await SwalHandled.showInfo(
        'Active Subscription Found',
        'To delete or change your payment method you\'ll need to cancel all your active subscriptions. Once canceled you can re-subscribe using your new card.'
      );
      return;
    }

    const isConfirmed = await SwalHandled.customConfirm(
      'Remove Card',
      'Are you sure you want to remove this card?',
      'Yes',
      'Cancel',
      {
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
      }
    );

    if (!isConfirmed) {
      return;
    }

    setError('');
    setIsDeletingCard(true);
    try {
      const response = await deleteUserCard(cardId);
      if (!response.success) {
        const errorMessage = response.error || 'Failed to delete card';
        setError(errorMessage);
        await SwalHandled.showError('Error', errorMessage);
        return;
      }

      const updatedCards = Array.isArray(cards)
        ? cards.filter((card) => card._id !== cardId)
        : [];
      setCards(updatedCards);
      
      // Re-check active subscriptions for remaining cards
      if (updatedCards.length > 0) {
        const subscriptionChecks = await Promise.all(
          updatedCards.map((card) => checkActiveSubscription(card._id))
        );
        const hasAnyActive = subscriptionChecks.some((hasActive) => hasActive === true);
        setHasActiveSubscription(hasAnyActive);
      } else {
        setHasActiveSubscription(false);
      }
    } catch (err) {
      const errorMessage = err?.message || 'Failed to delete card';
      setError(errorMessage);
      await SwalHandled.showError('Error', errorMessage);
    } finally {
      setIsDeletingCard(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
  };

  const handleContactSupport = useCallback(() => {
    navigate.toContactSupport();
  }, [navigate]);

  return (
    <div className="modern-wallet-page">
      <ModernHeader
        customTitle="Wallet"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <main className="modern-wallet-main">
        {/* Warning Notice */}
        {hasActiveSubscription && (
          <div className="notice_card">
            <div className="modern-wallet-warning-content">
              <i className="fa-solid fa-exclamation-triangle modern-wallet-warning-icon"></i>
              <p className="modern-wallet-warning-text">
                You will need to cancel all your active monthly subscriptions before you can delete your card.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="modern-wallet-error">
            <p className="modern-wallet-error-text">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="modern-wallet-cards-list">
            <div
              className="modern-wallet-card-item modern-wallet-card-item--loading"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="modern-wallet-loading">
                <span className="modern-wallet-spinner" aria-hidden="true"></span>
                <p className="modern-wallet-loading-text">Loading wallet information...</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {cards.length === 0 ? (
              <div className="modern-wallet-empty-state">
                <div className="modern-wallet-empty-icon">
                  <i className="fa-solid fa-credit-card"></i>
                </div>
                <h3 className="modern-wallet-empty-title">No Cards Found</h3>
                <p className="modern-wallet-empty-description">
                  {/* You haven't added any payment cards yet. Add a card to start making payments. */}
                  Slide in a card and let’s unlock the real fun 😏
                </p>
              </div>
            ) : (
              <div className={`modern-wallet-cards-list ${isDeletingCard ? 'modern-wallet-cards-list--deleting' : ''}`}>
                {isDeletingCard && (
                  <div className="modern-wallet-deleting-overlay">
                    <div className="modern-wallet-deleting-spinner"></div>
                    <p className="modern-wallet-deleting-text">Deleting card...</p>
                  </div>
                )}
                {cards.map((card, index) => (
                  <div key={index} className="modern-wallet-card-item">
                    <div className="modern-wallet-card-header">
                      <div className="modern-wallet-card-info">
                        <div className="modern-wallet-card-brand">
                          <div className="modern-wallet-card-icon-container">
                            <span className="modern-wallet-icon-wrapper">
                              <i className={`${getCardIcon(card.card_brand)} modern-wallet-card-icon`}></i>
                            </span>
                          </div>
                          <div className="modern-wallet-card-details">
                            <span className="modern-wallet-card-type">{card.card_brand}</span>
                            <p className="modern-wallet-card-label">
                              {card.is_default ? 'Primary Card' : 'Secondary Card'}
                            </p>
                          </div>
                          <div className="modern-wallet-card-actions">
                            <button
                              title="Delete"
                              onClick={() => handleDelete(card._id)}
                              className="modern-wallet-action-btn modern-wallet-delete-btn"
                              disabled={isDeletingCard}
                            >
                              <span className="modern-wallet-icon-wrapper">
                                <i className="fa-solid fa-trash-can"></i>
                              </span>
                            </button>
                          </div>
                        </div>
                        <div className="modern-wallet-card-content">
                          <div className="modern-wallet-card-number">
                            {formatCardNumber(card.last4)}
                          </div>
                          <div className="modern-wallet-card-footer">
                            <div className="modern-wallet-card-expiry">
                              Expires: {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                            </div>
                            <div className="modern-wallet-card-status">
                              <span className="modern-wallet-icon-wrapper">
                                <i className="fa-solid fa-circle-check modern-wallet-status-icon"></i>
                              </span>
                              <span className="modern-wallet-status-text">Verified</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Add Card Section - Positioned below cards */}
        <div className="modern-wallet-add-card-section">
          <button
            className={`modern-wallet-add-card-btn ${cards.length > 0 ? 'modern-wallet-add-card-btn-disabled' : ''}`}
            onClick={handleAddCard}
            disabled={cards.length > 0}
            title={cards.length > 0 ? 'You already have cards. Remove existing cards first.' : 'Add a new payment card'}
          >
            <i className="fa-solid fa-plus modern-wallet-add-icon"></i>
            <span className="modern-wallet-add-text">
              {cards.length > 0 ? 'Add Card' : 'Add Card'}
            </span>
          </button>
        </div>

        <div className="modern-wallet-billing-info">
          <div className="modern-wallet-billing-content">
            <i className="fa-solid fa-info-circle modern-wallet-billing-icon"></i>
            <div className="modern-wallet-billing-details">
              <h3 className="modern-wallet-billing-title">Billing Information</h3>
              <p className="modern-wallet-billing-text">
                The charges on your credit card statement will appear as{' '}
                <span className="modern-wallet-billing-highlight">*CCBill.com Prestige Int*</span>
              </p>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="modern-wallet-security-section">
          <div className="modern-wallet-security-header">
            <i className="fa-solid fa-shield-halved modern-wallet-security-icon"></i>
            <h3 className="modern-wallet-security-title">Security & Privacy</h3>
          </div>
          <p className="modern-wallet-security-text">
            Payments are handled securely through a trusted third party processor. Your details are protected with industry standard SSL encryption and your card information is never stored on our servers.
          </p>
        </div>

        {/* Support Section */}
        <div className="modern-wallet-support-section">
          <div className="modern-wallet-support-header">
            <span className="modern-wallet-icon-wrapper">
              <i className="fa-solid fa-headset modern-wallet-support-icon"></i>
            </span>
            <h3 className="modern-wallet-support-title">Need Help?</h3>
          </div>
          <p className="modern-wallet-support-text">
            Having issues with payments or need assistance with your wallet? Our support team is here to help.
          </p>
          <div className="d-flex justify-content-center">
          <button
            className="modern-wallet-contact-support-btn"
            onClick={handleContactSupport}
          >
            Contact Support
          </button>
          </div>
        </div>

      </main>

      {/* Add Card Modal */}
      <AddCardModal
        openModal={showAddModal}
        toggleModal={handleCloseModal}
        onSuccess={loadCards}
        onCancel={handleCloseModal}
        existingCardsCount={cards && cards.length > 0 ? cards.length : 0}
      />

    </div>
  );
};

export default ModernWallet;